/**
 * `/people` — the people directory, Task T2-B (pillar: manage people), consuming
 * Task T2-A's read-only `GET /employees`, `GET /employees/{employeeId}` and
 * `GET /employees/departments`.
 *
 * `nav/menu.ts` gates this item on the same anyOf `/attendance` and `/presence`
 * use — `[workforce.view_self, workforce.view_department, workforce.view_workspace]`
 * — with no role check: a plain employee reaches this screen too, and the server's
 * `resolved` scope (self ⊂ department ⊂ company) narrows the answer to exactly one
 * row, themself. That is the correct answer for a `view_self`-only session, not an
 * empty state and not an error — `api/employees.ts`'s own header states the same
 * contract.
 *
 * ── Filters live in the URL, deliberately ─────────────────────────────────────
 *
 * Every filter below (`q`, `status`, `department_id`, `role`, `sort`, `offset`) is a
 * **URL query parameter**, read through `useSearchParams` and written back on every
 * change — the same construction `DevicesScreen.tsx` and `OverviewScreen.tsx` use.
 * BambooHR's own "saved filters" are scoped per user, on the server; this screen's
 * equivalent is a URL, on the client — no server-side storage, but shareable by
 * pasting a link and bookmarkable by the browser, which a per-user saved filter is
 * not. `q` is the one exception to "written back immediately": it is debounced
 * 250ms after the last keystroke, so the URL (and the request) does not change on
 * every character typed.
 *
 * ── No productivity, no score, no payroll ─────────────────────────────────────
 *
 * `EmployeeDirectoryRow`'s own description: "mirror-only… those stay on their own
 * surfaces." Nothing on this screen reads a field those two endpoints do not
 * publish.
 */

import { useCallback, useEffect, useMemo, useState, type ReactNode } from 'react';
import { useSearchParams } from 'react-router';

import { useI18n } from '../i18n';
import { formatInstant } from '../i18n/datetime';
import { useSession } from '../auth/SessionProvider';
import {
  EMPLOYEE_ROLES,
  EMPLOYEE_SORTS,
  EMPLOYEE_STATUS_FILTERS,
  employeeDepartmentsPath,
  employeesPath,
  type EmployeeDepartmentFacetList,
  type EmployeeDirectoryList,
  type EmployeeRole,
  type EmployeeSort,
  type EmployeeStatusFilter,
} from '../api/employees';
import { useApiQuery } from '../api/useApiQuery';
import type { MessageKey } from '../i18n/messages';
import { EMPLOYEE_STATUS_PRESENTATION, employeeStatusOf } from '../people/status';
import { ApiErrorNote } from '../states/ApiErrorNote';
import { Badge } from '../states/Badge';
import { EmptyState } from '../states/EmptyState';
import { LastSync } from '../states/LastSync';
import { Skeleton } from '../states/Skeleton';
import { ScreenHeading } from '../shell/ScreenHeading';
import { PeopleDrawer } from './people/PeopleDrawer';

/** `openapi.yaml`'s `OffsetLimit` default is 50; explicit, for the reason every other screen's own page-size constant gives — a page boundary the screen did not choose is one the reader cannot see. */
export const PEOPLE_PAGE_LIMIT = 50;

const SEARCH_DEBOUNCE_MS = 250;

const ROLE_LABEL_KEY: Readonly<Record<EmployeeRole, MessageKey>> = {
  ceo: 'shell.role.ceo',
  manager: 'shell.role.manager',
  employee: 'shell.role.employee',
};

const STATUS_OPTION_LABEL_KEY: Readonly<Record<EmployeeStatusFilter, MessageKey>> = {
  active: 'people.filter.status.active',
  offboarded: 'people.filter.status.offboarded',
  all: 'people.filter.status.all',
};

const SORT_COLUMN_LABEL_KEY: Readonly<Record<EmployeeSort, MessageKey>> = {
  full_name: 'people.list.col.fullName',
  department: 'people.list.col.department',
  synced_at: 'people.list.col.syncedAt',
};

function statusFromParam(raw: string | null): EmployeeStatusFilter {
  return EMPLOYEE_STATUS_FILTERS.find((value) => value === raw) ?? 'active';
}

function roleFromParam(raw: string | null): EmployeeRole | null {
  return EMPLOYEE_ROLES.find((value) => value === raw) ?? null;
}

function sortFromParam(raw: string | null): EmployeeSort {
  return EMPLOYEE_SORTS.find((value) => value === raw) ?? 'full_name';
}

function offsetFromParam(raw: string | null): number {
  const parsed = raw === null ? NaN : Number.parseInt(raw, 10);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

function SortableHeader({
  column,
  activeSort,
  onSort,
  label,
  testId,
}: {
  readonly column: EmployeeSort;
  readonly activeSort: EmployeeSort;
  readonly onSort: (column: EmployeeSort) => void;
  readonly label: string;
  readonly testId: string;
}): ReactNode {
  const active = column === activeSort;
  // `synced_at` sorts DESC (`api/employees.ts`'s own header, transcribed from
  // `listEmployees`); the other two sort ASC. `aria-sort` reflects the direction
  // that is actually in effect, not a generic "sorted".
  const direction = column === 'synced_at' ? 'descending' : 'ascending';
  return (
    <th scope="col" aria-sort={active ? direction : 'none'}>
      <button
        type="button"
        className="wp-linklike"
        onClick={() => {
          onSort(column);
        }}
        data-testid={testId}
      >
        {label}
      </button>
    </th>
  );
}

export function PeopleScreen(): ReactNode {
  const { t, locale } = useI18n();
  const { user } = useSession();
  const timezone = user?.timezone ?? 'UTC';
  const [params, setParams] = useSearchParams();

  const status = statusFromParam(params.get('status'));
  const departmentId = params.get('department_id');
  const role = roleFromParam(params.get('role'));
  const sort = sortFromParam(params.get('sort'));
  const offset = offsetFromParam(params.get('offset'));
  const urlQ = params.get('q') ?? '';

  const [qInput, setQInput] = useState(urlQ);
  const [openId, setOpenId] = useState<string | null>(null);

  const setParam = useCallback(
    (updates: Readonly<Record<string, string | null>>) => {
      setParams((prev) => {
        const next = new URLSearchParams(prev);
        for (const [key, value] of Object.entries(updates)) {
          if (value === null) next.delete(key);
          else next.set(key, value);
        }
        return next;
      });
    },
    [setParams],
  );

  // Debounced search: 250ms after the last keystroke, the URL's own `q` changes
  // (never on every keystroke), and the page resets to the first one — a narrower
  // search applied at, say, offset 200 would show the fifth page of a result set
  // nobody has seen page one of.
  // `setParam` and `urlQ` are deliberately not in the dependency array: this
  // effect must fire once per settled keystroke, not once per URL change it
  // itself just caused (which would re-arm the same timer for no new input).
  useEffect(() => {
    const timer = window.setTimeout(() => {
      const trimmed = qInput.trim();
      setParam({ q: trimmed === '' ? null : trimmed, offset: null });
    }, SEARCH_DEBOUNCE_MS);
    return () => {
      window.clearTimeout(timer);
    };
  }, [qInput]);

  const listPath = employeesPath({
    q: urlQ === '' ? undefined : urlQ,
    status,
    departmentId: departmentId ?? undefined,
    role: role ?? undefined,
    sort,
    limit: PEOPLE_PAGE_LIMIT,
    offset,
  });

  const list = useApiQuery<EmployeeDirectoryList>(listPath);
  const departments = useApiQuery<EmployeeDepartmentFacetList>(employeeDepartmentsPath());

  const rows = list.data?.employees ?? [];
  const total = list.data?.total ?? 0;

  // The manager's name, resolved from rows already on this page — there is no
  // per-id directory lookup here, and one `readEmployee` per row would be an N+1
  // read for a fact the page may already carry. A manager outside this page (or
  // this filter) renders their id, honestly, rather than a name fetched from
  // nowhere on this screen.
  const nameById = useMemo(() => {
    const map = new Map<string, string>();
    for (const row of rows) map.set(row.id, row.full_name);
    return map;
  }, [rows]);

  /** The newest achau stamp on this page — what `LastSync` prints beside the title. */
  const newestSyncedAt = useMemo(() => {
    let newest: string | null = null;
    for (const row of rows) {
      if (newest === null || row.synced_at > newest) newest = row.synced_at;
    }
    return newest;
  }, [rows]);

  const onSort = useCallback(
    (column: EmployeeSort) => {
      setParam({ sort: column === 'full_name' ? null : column, offset: null });
    },
    [setParam],
  );

  const rangeFrom = total === 0 ? 0 : offset + 1;
  const rangeTo = Math.min(offset + PEOPLE_PAGE_LIMIT, total);

  return (
    <div className="wp-stack">
      <div className="wp-screen-head">
        <ScreenHeading>{t('nav.people')}</ScreenHeading>
        {/* State 9 of 9, printed beside the title rather than under the table: the
            directory is a *mirror* of achau, so the newest `synced_at` on this page is
            the honest achau stamp. There is no agent heartbeat on this screen — this
            surface reads `/employees` and nothing device-shaped — so that half renders
            as "chưa từng" the way `/my-activity` and `/time-tracking` already leave it,
            rather than being invented. Flagged in the group report. */}
        <LastSync agentSyncAt={null} achauSyncAt={newestSyncedAt} timezone={timezone} />
      </div>

      <p className="wp-small wp-muted" data-testid="people-intro">
        {t('people.intro')}
      </p>

      <fieldset className="wp-filters wp-filters--bar" data-testid="people-filters">
        {/* Printed, this was an 11px caps eyebrow sitting flush on top of the first
            field's own label — two keys for one control (finish review, minor b). The
            legend stays in the DOM because a `<fieldset>` without one announces as an
            unnamed group. */}
        <legend className="wp-visually-hidden">{t('people.filters.legend')}</legend>

        <div className="wp-field">
          <label htmlFor="people-search">{t('people.filter.search')}</label>
          <input
            id="people-search"
            type="search"
            value={qInput}
            onChange={(event) => {
              setQInput(event.target.value);
            }}
            data-testid="people-search"
          />
        </div>

        <div className="wp-field">
          <label htmlFor="people-status">{t('people.filter.status')}</label>
          <select
            id="people-status"
            value={status}
            onChange={(event) => {
              const next = event.target.value as EmployeeStatusFilter;
              setParam({ status: next === 'active' ? null : next, offset: null });
            }}
            data-testid="people-status"
          >
            {EMPLOYEE_STATUS_FILTERS.map((value) => (
              <option key={value} value={value}>
                {t(STATUS_OPTION_LABEL_KEY[value])}
              </option>
            ))}
          </select>
        </div>

        <div className="wp-field">
          <label htmlFor="people-department">{t('people.filter.department')}</label>
          <select
            id="people-department"
            value={departmentId ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              setParam({ department_id: value === '' ? null : value, offset: null });
            }}
            data-testid="people-department"
          >
            <option value="">{t('people.filter.department.all')}</option>
            {/* A facet with `department_id: null` (employees with no department, sorted
                NULLS LAST) is not offered as a filter option: `employees.ts`'s own
                `department_id` query param is an exact match on a real id and has no way
                to ask for "no department" — an option here would collide with "All
                departments" on the empty-string value AND silently do nothing when
                picked. Its `active_count` is still real information, just not a filter
                this screen can express, so it is left off the list rather than offered
                as one that cannot work. */}
            {(departments.data?.departments ?? [])
              .filter((facet) => facet.department_id !== null)
              .map((facet) => (
                <option key={facet.department_id} value={facet.department_id ?? ''}>
                  {t('people.filter.department.option', {
                    name: facet.department_name ?? t('people.noDepartment'),
                    count: facet.active_count,
                  })}
                </option>
              ))}
          </select>
        </div>

        <div className="wp-field">
          <label htmlFor="people-role">{t('people.filter.role')}</label>
          <select
            id="people-role"
            value={role ?? ''}
            onChange={(event) => {
              const value = event.target.value;
              setParam({ role: value === '' ? null : value, offset: null });
            }}
            data-testid="people-role"
          >
            <option value="">{t('people.filter.role.all')}</option>
            {EMPLOYEE_ROLES.map((value) => (
              <option key={value} value={value}>
                {t(ROLE_LABEL_KEY[value])}
              </option>
            ))}
          </select>
        </div>
      </fieldset>

      <section className="wp-section" aria-labelledby="people-list-heading">
        <h2 className="wp-section__title" id="people-list-heading">
          {t('people.list.heading')}
        </h2>

        {list.status === 'loading' ? (
          <Skeleton
            rows={5}
            variant="block"
            label={t('people.list.loading')}
            testId="people-list-skeleton"
          />
        ) : null}

        {list.status === 'error' && list.error !== null ? (
          <ApiErrorNote
            error={list.error}
            onRetry={list.reload}
            subject={t('people.list.subject')}
            testId="people-list-error"
          />
        ) : null}

        {list.status === 'ok' && rows.length === 0 ? (
          <EmptyState
            title={t('people.list.empty.title')}
            description={t('people.list.empty.body')}
            testId="people-list-empty"
          />
        ) : null}

        {list.status === 'ok' && rows.length > 0 ? (
          <>
            <div className="wp-table-scroll">
              <table className="wp-table wp-table--wide" data-testid="people-list">
                <caption className="wp-visually-hidden">{t('people.list.caption')}</caption>
                <thead>
                  <tr>
                    <SortableHeader
                      column="full_name"
                      activeSort={sort}
                      onSort={onSort}
                      label={t(SORT_COLUMN_LABEL_KEY.full_name)}
                      testId="people-sort-full_name"
                    />
                    <th scope="col">{t('people.list.col.email')}</th>
                    <th scope="col">{t('people.list.col.role')}</th>
                    <SortableHeader
                      column="department"
                      activeSort={sort}
                      onSort={onSort}
                      label={t(SORT_COLUMN_LABEL_KEY.department)}
                      testId="people-sort-department"
                    />
                    <th scope="col">{t('people.list.col.jobTitle')}</th>
                    <th scope="col">{t('people.list.col.manager')}</th>
                    <th scope="col">{t('people.list.col.status')}</th>
                    <SortableHeader
                      column="synced_at"
                      activeSort={sort}
                      onSort={onSort}
                      label={t(SORT_COLUMN_LABEL_KEY.synced_at)}
                      testId="people-sort-synced_at"
                    />
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row) => {
                    const displayStatus = employeeStatusOf(row);
                    const managerName =
                      row.manager_employee_id === null
                        ? null
                        : (nameById.get(row.manager_employee_id) ?? row.manager_employee_id);
                    return (
                      <tr key={row.id} data-testid={`people-row-${row.id}`}>
                        <th scope="row">
                          <button
                            type="button"
                            className="wp-linklike"
                            data-testid={`people-open-${row.id}`}
                            onClick={() => {
                              setOpenId(row.id);
                            }}
                          >
                            {row.full_name}
                          </button>
                        </th>
                        <td>{row.email}</td>
                        <td>{t(ROLE_LABEL_KEY[row.role])}</td>
                        <td data-testid={`people-department-${row.id}`}>
                          {row.department_name ?? t('people.noDepartment')}
                        </td>
                        <td>{row.job_title ?? '—'}</td>
                        <td data-testid={`people-manager-${row.id}`}>
                          {managerName ?? t('people.noManager')}
                        </td>
                        <td data-testid={`people-status-${row.id}`}>
                          <Badge
                            severity={EMPLOYEE_STATUS_PRESENTATION[displayStatus].severity}
                            icon={EMPLOYEE_STATUS_PRESENTATION[displayStatus].icon}
                            label={t(EMPLOYEE_STATUS_PRESENTATION[displayStatus].labelKey)}
                            tone={EMPLOYEE_STATUS_PRESENTATION[displayStatus].tone}
                          />
                        </td>
                        <td className="wp-audit__when" data-testid={`people-synced-${row.id}`}>
                          {formatInstant(row.synced_at, locale, timezone)}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>

            <div className="wp-pagination" data-testid="people-pagination">
              <button
                type="button"
                className="wp-button"
                data-testid="people-prev"
                disabled={offset === 0}
                onClick={() => {
                  const prevOffset = Math.max(offset - PEOPLE_PAGE_LIMIT, 0);
                  setParam({ offset: prevOffset === 0 ? null : String(prevOffset) });
                }}
              >
                {t('people.pagination.prev')}
              </button>
              <span data-testid="people-range">
                {t('people.pagination.range', { from: rangeFrom, to: rangeTo, total })}
              </span>
              <button
                type="button"
                className="wp-button"
                data-testid="people-next"
                disabled={offset + PEOPLE_PAGE_LIMIT >= total}
                onClick={() => {
                  setParam({ offset: String(offset + PEOPLE_PAGE_LIMIT) });
                }}
              >
                {t('people.pagination.next')}
              </button>
            </div>
          </>
        ) : null}
      </section>

      {openId === null ? null : (
        <PeopleDrawer
          employeeId={openId}
          onClose={() => {
            setOpenId(null);
          }}
          onSelectEmployee={(id) => {
            setOpenId(id);
          }}
        />
      )}
    </div>
  );
}
